// -----------------------------------------------------------
// FloraIQ - Methodology Quality (Aba 8: Qualidade Metodológica)
// -----------------------------------------------------------

import { type FC, useState, useEffect } from "react";
import { ladmValidationRules, ladmInconsistencyCards } from "@/mocks/ladm";
import LadmStatusBadge from "./LadmStatusBadge";
import type { LadmBadgeVariant } from "./LadmStatusBadge";

interface MethodsQualitySectionProps {
  autoValidate?: boolean;
}

const priorityMap: Record<string, LadmBadgeVariant> = {
  critica: "critica",
  alta: "alta",
  media: "media",
  baixa: "baixa",
};

const MethodsQualitySection: FC<MethodsQualitySectionProps> = ({ autoValidate = false }) => {
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (autoValidate && !validated) {
      setValidated(true);
    }
  }, [autoValidate, validated]);

  return (
    <div className="space-y-6">
      {/* Cards de Inconsistências */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ladmInconsistencyCards.map((card) => (
          <div
            key={card.title}
            className={`bg-white rounded-card border p-4 cursor-pointer hover:shadow-sm transition-all duration-200 ${
              card.color === "danger" ? "border-red-200/70 bg-red-50/30 hover:bg-red-50/50" :
              card.color === "warning" ? "border-orange-200/70 bg-orange-50/30 hover:bg-orange-50/50" :
              "border-yellow-200/70 bg-yellow-50/30 hover:bg-yellow-50/50"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <i className={`${card.icon} text-sm ${
                card.color === "danger" ? "text-red-600" :
                card.color === "warning" ? "text-orange-600" :
                "text-yellow-600"
              }`}></i>
              <span className="text-xs font-medium text-foreground-700 truncate">{card.title}</span>
            </div>
            <p className={`text-xl font-bold font-heading ${
              card.color === "danger" ? "text-red-700" :
              card.color === "warning" ? "text-orange-700" :
              "text-yellow-700"
            }`}>{card.value.toLocaleString("pt-BR")}</p>
          </div>
        ))}
      </div>

      {/* Botão de validação */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setValidated(!validated)}
          className="btn-primary text-xs px-4 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-check-double-line mr-1.5"></i>
          {validated ? "Re-executar validação" : "Executar validação metodológica"}
        </button>
        {validated && (
          <span className="text-xs text-green-700 font-medium fade-in">
            <i className="ri-check-line mr-1"></i>Validação concluída — {ladmValidationRules.length} regras verificadas
          </span>
        )}
      </div>

      {/* Tabela de regras */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/50">
          <h3 className="text-sm font-semibold text-foreground-900 font-heading">Regras de Consistência Metodológica</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100/70">
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading">Regra</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Total Avaliado</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Conformes</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Inconformes</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">%</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Prioridade</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Ação Recomendada</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {ladmValidationRules.map((rule) => (
                <tr key={rule.id} className="hover:bg-background-50/60 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-foreground-700 max-w-[260px]">{rule.regra}</td>
                  <td className="px-4 py-2.5 text-center text-xs font-mono text-foreground-700">{rule.totalAvaliado.toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-2.5 text-center text-xs font-mono text-green-700">{rule.conformes.toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-2.5 text-center text-xs font-mono text-red-700">{rule.inconformes.toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-xs font-semibold ${rule.percentual >= 95 ? "text-green-700" : rule.percentual >= 85 ? "text-orange-600" : "text-red-600"}`}>
                      {rule.percentual}%
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <LadmStatusBadge variant={priorityMap[rule.prioridade] || "media"} />
                  </td>
                  <td className="px-4 py-2.5 text-xs text-foreground-600 max-w-[280px]">{rule.acaoRecomendada}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MethodsQualitySection;